import { Box, Typography } from "@mui/material";
import { IModifierGroup } from "../../../types/menu";
import { useInfoStore } from "../../../store/userInfo";
import { useTranslation } from "react-i18next";

export const RenderGroupState = ({ group, level }: { group: IModifierGroup; level: number }) => {
  const {userInfo, currentLng} = useInfoStore();
  const storeId = userInfo?.storeId;
  const {t} = useTranslation(`${storeId}`);
    // (1) 이 그룹(및 하위 그룹)의 충족 결과
    const {
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied,
			currentQuantity
    } = checkGroupSatisfiedRecursively(group);

    // (2) 렌더할 태그 (초록/빨강/주황)
    const tagElement = renderRequiredTag({
      isRequired: group.isRequired,
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
			currentQuantity
    });

    // (3) 실제 JSX 렌더
    return (
      <Box
        key={group.path}
        sx={{
          display: 'flex',
          flexDirection: 'column',
					justifyContent: 'flex-start',
					alignItems: 'flex-start',
          borderRadius: '.4rem',
					pl : `${(level -1) * .8}rem`
        }}
      >
				{(group.isRequired || (!group.isRequired && currentQuantity > 0)) && (
        <Box sx={{display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: '.1rem' }}>
            {group.modifierGroupName}
          </Typography>

          {tagElement}
        </Box>)}

        {/* (4) modifiers 반복 출력 */}
        <Box sx={{ ml: 1 }}>
          {group.modifiers.map((modifier) => {
            return (
              <Box key={modifier.path} sx={{ mb: 0 }}>
                {/* (5) modifier 하위에 nestedGroup 존재 시 → 재귀 렌더 */}
                {modifier.hasNestedModifierGroups && modifier.modifiers.length > 0 && (
                  <Box sx={{ ml: 0 }}>
                    {modifier.modifiers.map((nestedGroup) => RenderGroupState({ group: nestedGroup, level: level + 1 }))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  export const renderTopGroupState = (
    t: (key: string) => string,
    storeId: string,
    currentLng: string,
    group: IModifierGroup,
    groupIdx: number
  ) => {
    // (1) 이 그룹(및 하위 그룹)의 충족 결과
    const {
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied,
    } = checkTopGroupSatisfiedRecursively(group);

    // (2) 렌더할 태그 (초록/빨강/주황)
    const tagElement = renderAllRequiredTag({
      isRequired: group.isRequired,
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied
    });

    // (3) 실제 JSX 렌더
    return (
      <Box
        key={group.path}
        sx={{
          display: 'flex',
          flexDirection: 'column',
					justifyContent: 'flex-start',
					alignItems: 'flex-start',
					flex :1,
        }}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
					<Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', width: '100%', mb: 1}}>
						<Box sx={{position: 'relative', mr: '1.0rem', width: '2.8rem', height: '2.8rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
							<img style={{position: 'absolute', top: 0, left: 0, width: '2.8rem', height: '2.8rem' }}
									src={process.env.PUBLIC_URL+'/assets/images/ellipse.svg'}
									alt="ellipse"
								/>
							<Typography sx={{position: 'relative', fontSize: '1.8rem', fontWeight: '600', color: '#fff', zIndex: '1'}}>{groupIdx + 1}</Typography>
						</Box>
						{tagElement}
					</Box>
          <Typography variant="subtitle1" sx={{ fontSize: '1.5rem', fontWeight: 'bold', mr: '.1rem', textAlign: 'start', width: '100%', ml: '1.0rem' }}>
            {t(`${storeId}:optionGroupNames.${group.id}.${currentLng}`)}
            {/* {group.modifierGroupName} */}
          </Typography>
        </Box>
      </Box>
    );
  }

  interface RequiredTagProps {
    isRequired: boolean;
    satisfiesMinRequirement: boolean;
    satisfiesMaxRequirement: boolean;
		currentQuantity: number;
  } 

  const renderRequiredTag =({
    isRequired,
    satisfiesMinRequirement,
    satisfiesMaxRequirement,
		currentQuantity
  }: RequiredTagProps) => {
    if (isRequired) {
      // (a) 필수 그룹
      if (satisfiesMinRequirement && satisfiesMaxRequirement) {
        // min/max 모두 충족 => 초록색 + done.svg
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '10.0rem',
              height: '2.4rem',
              padding: '.2rem .9rem',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '3.0rem',
              background: '#66C03A',
            }}
          >
            <img style={{width: '1.4rem', height: '1.4rem'}}
              src={`${process.env.PUBLIC_URL}/assets/images/done.svg`}
              alt="done"
            />
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                color: '#fff',
                ml: '.1rem',
              }}
            >
              Required
            </Typography>
          </Box>
        );
      } else {
        // (b) 필수 but min/max 불충족 => 빨간색 + warning.svg
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '10.0rem',
              height: '2.4rem',
              padding: '.2rem .9rem',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '3.0rem',
              background: '#F72E13',
            }}
          >
            <img style={{width: '1.4rem', height: '1.4rem'}}
              src={`${process.env.PUBLIC_URL}/assets/images/warning.svg`}
              alt="warning"
            />
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                color: '#fff',
                ml: 1,
              }}
            >
              Required
            </Typography>
          </Box>
        );
      }
    } else {
      // (c) Optional 그룹 => 주황색 배지

      return (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							width: '7.1rem',
							height: '2.4rem',
							padding: '.2rem .9rem',
							justifyContent: 'center',
							alignItems: 'center',
							borderRadius: '3.0rem',
							background: '#FF8E0A',
						}}
					>
						<Typography
							sx={{
								fontSize: '1.4rem',
								fontStyle: 'normal',
								fontWeight: '600',
								lineHeight: 'normal',
								color: '#fff',
							}}
						>
							Optional
						</Typography>
					</Box>
      );
    }
  }

  interface AllRequiredTagProps {
    isRequired: boolean;
		satisfiesMinRequirement: boolean;
    satisfiesMaxRequirement: boolean;
    isFullySatisfied: boolean;
  } 

  const renderAllRequiredTag =({
    isRequired,
		satisfiesMinRequirement,
    satisfiesMaxRequirement,
    isFullySatisfied
  }:AllRequiredTagProps) => {
    if (isRequired) {
      // (a) 필수 그룹
      if (isFullySatisfied && satisfiesMinRequirement && satisfiesMaxRequirement) {
        // min/max 모두 충족 => 초록색 + done.svg
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '10.0rem',
              height: '2.4rem',
              padding: '.2rem .9rem',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '3.0rem',
              background: '#66C03A',
            }}
          >
            <img style={{width: '1.4rem', height: '1.4rem'}}
              src={`${process.env.PUBLIC_URL}/assets/images/done.svg`}
              alt="done"
            />
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                color: '#fff',
                ml: '.1rem',
              }}
            >
              Required
            </Typography>
          </Box>
        );
      } else {
        // (b) 필수 but min/max 불충족 => 빨간색 + warning.svg
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '10.0rem',
              height: '2.4rem',
              padding: '.2rem .9rem',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '3.0rem',
              background: '#F72E13',
            }}
          >
            <img style={{width: '1.4rem', height: '1.4rem'}}
              src={`${process.env.PUBLIC_URL}/assets/images/warning.svg`}
              alt="warning"
            />
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: 'normal',
                color: '#fff',
                ml: '.1rem',
              }}
            >
              Required
            </Typography>
          </Box>
        );
      }
    } else {
      // (c) Optional 그룹 => 주황색 배지
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '8.1rem',
            height: '2.4rem',
            padding: '.2rem .9rem',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '3.0rem',
            background: '#FF8E0A',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.4rem',
              fontStyle: 'normal',
              fontWeight: '600',
              lineHeight: 'normal',
              color: '#fff',
            }}
          >
            Optional
          </Typography>
        </Box>
      );
    }
  }

  /**
   * 특정 ModifierGroup이 자기 자신(및 하위 그룹) 포함, 
   * min/max/required 조건을 충족하는지 재귀적으로 검사
   *
   * @param group 검사 대상 IModifierGroup
   * @returns { 
	 *	satisfiesMinRequirement: boolean;
	 *	satisfiesMaxRequirement: boolean;
	 *	isFullySatisfied: boolean; 
	 *  currentQuantity: number;
  * }
  */
  const checkGroupSatisfiedRecursively = (group: IModifierGroup) => {

  // minRequired, maxAllowed 가 null/undefined 라면 => min=0 / max=Infinity 로 처리
  const minReq = group.minRequired ?? 0;
  const maxReq = group.maxAllowed ?? Infinity;
  
	const currentQuantity = group.modifiers.reduce(
    (sum: number, modifier: any) => sum + (modifier.quantity ?? 0),
    0
  );

  const satisfiesMinRequirement = currentQuantity >= minReq;
  const satisfiesMaxRequirement = currentQuantity <= maxReq;

  // (2) 우선 현재 그룹 자체의 min/max가 불충족이면
  //     => 이 그룹은 일단 "조건 미충족"
  let isFullySatisfied = true;
  if (group.isRequired || minReq > 0) {
    // 필수 그룹이거나 minReq>0 인 경우 → minReq 불충족이면 바로 false
    if (!satisfiesMinRequirement) {
      isFullySatisfied = false;
    }
  }
  // maxReq도 넘어가면 불충족
  if (!satisfiesMaxRequirement) {
    isFullySatisfied = false;
  }

	if (!isFullySatisfied) {
    return {
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied: false,
			currentQuantity
    };
  }

  // (3) 하위 그룹(nestedGroup) 검사
  //     Modifier가 hasNestedModifierGroups=true 이면, modifier.modifiers 배열 내 그룹들을 재귀 검사
  for (const modifier of group.modifiers) {
    if (modifier.hasNestedModifierGroups && modifier.modifiers.length > 0) {
      // 해당 modifier의 하위 그룹들
      for (const nestedGroup of modifier.modifiers) {
        const nestedResult = checkGroupSatisfiedRecursively(nestedGroup);
        // 하나라도 미충족이면 => 상위 그룹도 전체적으로 미충족
        if (!nestedResult.isFullySatisfied) {
          isFullySatisfied = false;
        }
      }
    }
  }

  return {
    satisfiesMinRequirement,
    satisfiesMaxRequirement,
    isFullySatisfied,
		currentQuantity
  };
}

const checkTopGroupSatisfiedRecursively = (group: IModifierGroup) => {

  // minRequired, maxAllowed 가 null/undefined 라면 => min=0 / max=Infinity 로 처리
  const minReq = group.minRequired ?? 0;
  const maxReq = group.maxAllowed ?? Infinity;
  
	const currentQuantity = group.modifiers.reduce(
    (sum: number, modifier: any) => sum + (modifier.quantity ?? 0),
    0
  );

  const satisfiesMinRequirement = currentQuantity >= minReq;
  const satisfiesMaxRequirement = currentQuantity <= maxReq;

  // (2) 우선 현재 그룹 자체의 min/max가 불충족이면
  //     => 이 그룹은 일단 "조건 미충족"
  let isFullySatisfied = true;
  if (group.isRequired || minReq > 0) {
    // 필수 그룹이거나 minReq>0 인 경우 → minReq 불충족이면 바로 false
    if (!satisfiesMinRequirement) {
      isFullySatisfied = false;
    }
  }
  // maxReq도 넘어가면 불충족
  if (!satisfiesMaxRequirement) {
    isFullySatisfied = false;
  }

	if (!isFullySatisfied) {
    return {
      groupId: group.id,
      currentQuantity,
      satisfiesMinRequirement,
      satisfiesMaxRequirement,
      isFullySatisfied: false,
    };
  }

  // (3) 하위 그룹(nestedGroup) 검사
  //     Modifier가 hasNestedModifierGroups=true 이면, modifier.modifiers 배열 내 그룹들을 재귀 검사
  for (const modifier of group.modifiers) {
    if (modifier.hasNestedModifierGroups && modifier.modifiers.length > 0) {
      // 해당 modifier의 하위 그룹들
      for (const nestedGroup of modifier.modifiers) {
        const nestedResult = checkGroupSatisfiedRecursively(nestedGroup);
        // 하나라도 미충족이면 => 상위 그룹도 전체적으로 미충족
        if (!nestedResult.isFullySatisfied) {
          isFullySatisfied = false;
        }
      }
    }
  }

  return {
    satisfiesMinRequirement,
    satisfiesMaxRequirement,
    isFullySatisfied,
  };
}

// Helper function that doesn't use hooks
export const renderGroupState = (group: IModifierGroup, level: number) => {
  return <RenderGroupState group={group} level={level} />;
}